// src/pages/CreateVideo.js
// Video studio → POST /api/videos/generate then poll GET /api/videos/{id}.
// Async: shows a live "Rendering…" state until the backend reports completed.

import { useEffect, useState } from "react";
import { videos, drift, models as modelsApi, multishot } from "../api";
import { uploadToTempUrl } from "../lib/upload";

const MODES = [
  { id: "text-to-video", label: "Text → Video" },
  { id: "frames", label: "Frames" },
  { id: "motion", label: "Motion" },
];
const ASPECTS = ["16:9", "9:16", "1:1"];
const RES = ["720p", "1080p"];

export default function CreateVideo({ user, onCreditsChange, remix, onNeedCredits }) {
  // Remix source: post top-level first, then recreate_payload (matches mobile).
  const post = remix || {};
  const seed = post.recreate_payload || {};
  const [mode, setMode] = useState(seed.generation_mode || post.generation_mode || "text-to-video");
  const [prompt, setPrompt] = useState(seed.prompt || post.prompt || "");
  const [duration, setDuration] = useState(seed.duration || post.duration || 5);
  const [aspect, setAspect] = useState(seed.aspect_ratio || post.aspect_ratio || "16:9");
  const [resolution, setResolution] = useState(seed.resolution || post.resolution || "720p");
  const [audio, setAudio] = useState(!!(seed.audio_enabled ?? post.audio_enabled));
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [video, setVideo] = useState(null);
  const [err, setErr] = useState("");
  const [lowCredits, setLowCredits] = useState(false);
  const [listingId, setListingId] = useState(seed.listing_id || post.listing_id || "");
  const [modelList, setModelList] = useState([]);
  const [multiShot, setMultiShot] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [imageUrl, setImageUrl] = useState(seed.image_url || seed.source_image_url || post.image_url || "");
  const [uploadingImg, setUploadingImg] = useState(false);

  // Advanced (restored on remix from recreate_payload)
  const [negativePrompt, setNegativePrompt] = useState(seed.negative_prompt || "");
  const [audioUrl, setAudioUrl] = useState(seed.audio_url || "");
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [cfgScale, setCfgScale] = useState(seed.cfg_scale ?? "");
  const [shotType, setShotType] = useState(seed.shot_type || "");
  const [voiceControl, setVoiceControl] = useState(!!seed.voice_control);
  const [startImageUrl, setStartImageUrl] = useState(seed.start_image_url || "");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const needsImage = mode === "frames" || mode === "motion";

  const pickAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAudio(true);
    setErr("");
    try {
      const r = await videos.uploadAudio(file);
      setAudioUrl(r.audio_url);
      setAudio(true);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setUploadingAudio(false);
    }
  };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    setErr("");
    try {
      setImageUrl(await uploadToTempUrl(file));
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setUploadingImg(false);
    }
  };

  useEffect(() => {
    modelsApi.video().then((d) => setModelList(d.models || [])).catch(() => {});
  }, []);

  const expandScenes = async () => {
    if (!prompt.trim() || expanding) return;
    setExpanding(true);
    setErr("");
    try {
      const r = await multishot.generate({ prompt, duration: Number(duration), max_scenes: 3 });
      if (r.prompt) {
        setPrompt(r.prompt);
        setMultiShot(true);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setExpanding(false);
    }
  };

  const generate = async () => {
    if (busy) return;
    if (needsImage && !imageUrl) {
      setErr("Upload a source image for this mode.");
      return;
    }
    if (!needsImage && !prompt.trim()) return;
    setBusy(true);
    setErr("");
    setLowCredits(false);
    setVideo(null);
    setStatus("Submitting…");
    try {
      const result = await videos.generateAndWait(
        {
          prompt,
          duration: Number(duration),
          aspect_ratio: aspect,
          resolution,
          audio_enabled: audio,
          generation_mode: mode,
          image_url: imageUrl || undefined,
          motion_preset_id: mode === "motion" ? seed.motion_preset_id || null : null,
          audio_url: audioUrl || undefined,
          negative_prompt: negativePrompt || undefined,
          cfg_scale: cfgScale !== "" ? Number(cfgScale) : undefined,
          shot_type: shotType || undefined,
          voice_control: voiceControl,
          start_image_url: startImageUrl || undefined,
          // pass-through so a "director" remix keeps its scenes even without full UI
          elements: seed.elements || undefined,
          multi_prompt: seed.multi_prompt || undefined,
          listing_id: listingId || undefined,
          smart_multi_shot: multiShot,
          multi_shot_prompt: multiShot ? prompt : undefined,
          source_drift_id: remix?._id,
        },
        {
          onTick: (v) => {
            const s = (v.status || "processing").toLowerCase();
            setStatus(v.progress != null ? `Rendering… ${v.progress}%` : `Rendering… (${s})`);
          },
        }
      );
      if ((result.status || "").toLowerCase() === "failed") {
        setErr(result.error || "Generation failed.");
      } else {
        setVideo(result);
        if (result.credits_remaining != null) onCreditsChange?.(result.credits_remaining);
      }
    } catch (e) {
      if (e.detail?.error === "INSUFFICIENT_CREDITS") {
        setErr(`Not enough credits — you need ${e.detail.credits_required}, have ${e.detail.credits_available}.`);
        setLowCredits(true);
      } else {
        setErr(e.message);
      }
    } finally {
      setBusy(false);
      setStatus("");
    }
  };

  const videoUrl = video?.video_url || video?.url;

  return (
    <div className="page">
      <div className="page__head">
        <h1>Create video</h1>
        <p className="page__sub">{remix ? "Remixing a Drift video." : "Text to video, image frames, or motion control."}</p>
      </div>

      <div className="tabs">
        {MODES.map((m) => (
          <button key={m.id} className={`tab ${mode === m.id ? "tab--on" : ""}`} onClick={() => setMode(m.id)}>{m.label}</button>
        ))}
      </div>

      <div className="studio">
        <div className="studio__form">
          {needsImage && (
            <div className="field">
              <span>Source image {mode === "frames" ? "(first frame)" : "(to animate)"}</span>
              {imageUrl ? (
                <div className="uploader__thumb" style={{ width: 110, height: 110 }}>
                  <img src={imageUrl} alt="source" />
                  <button onClick={() => setImageUrl("")}>✕</button>
                </div>
              ) : (
                <label className="uploader__drop" style={{ width: 110, height: 110 }}>
                  {uploadingImg ? "Uploading…" : "+ Upload image"}
                  <input type="file" accept="image/*" hidden onChange={pickImage} />
                </label>
              )}
            </div>
          )}

          <label className="field">
            <span>Prompt{needsImage ? " (optional)" : ""}</span>
            <textarea rows={multiShot ? 6 : 3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Slow dolly through a misty forest at dawn…" />
          </label>

          <button className="btn btn--ghost btn--block" onClick={expandScenes} disabled={expanding || !prompt.trim()}>
            {expanding ? "Directing…" : "🎬 Expand into multi-shot scenes"}
          </button>

          {modelList.length > 0 && (
            <div className="field">
              <span>Model</span>
              <select className="sort" style={{ width: "100%" }} value={listingId} onChange={(e) => setListingId(e.target.value)}>
                <option value="">Auto (balanced)</option>
                {modelList.map((m) => <option key={m.listing_id} value={m.listing_id}>{m.display_name}</option>)}
              </select>
            </div>
          )}

          <label className="field">
            <span>Duration · {duration}s</span>
            <input type="range" min="3" max="15" step="1" value={duration} onChange={(e) => setDuration(e.target.value)} />
          </label>

          <div className="field">
            <span>Resolution</span>
            <div className="seg">
              {RES.map((r) => <button key={r} className={resolution === r ? "seg__on" : ""} onClick={() => setResolution(r)}>{r}</button>)}
            </div>
          </div>

          <div className="field">
            <span>Aspect ratio</span>
            <div className="seg">
              {ASPECTS.map((a) => <button key={a} className={aspect === a ? "seg__on" : ""} onClick={() => setAspect(a)}>{a}</button>)}
            </div>
          </div>

          <button className={`toggle ${audio ? "toggle--on" : ""}`} onClick={() => setAudio((v) => !v)}>
            <span>🔊 Generate audio</span>
            <span className="toggle__knob" />
          </button>

          <button className="link-btn" style={{ alignSelf: "flex-start" }} onClick={() => setShowAdvanced((v) => !v)}>
            {showAdvanced ? "▾ Hide advanced" : "▸ Advanced options"}
          </button>

          {showAdvanced && (
            <>
              <label className="field">
                <span>Negative prompt</span>
                <textarea rows={2} value={negativePrompt} onChange={(e) => setNegativePrompt(e.target.value)} placeholder="What to avoid…" />
              </label>

              <div className="field">
                <span>Custom audio track</span>
                {audioUrl ? (
                  <div className="row-input">
                    <input type="text" value={audioUrl} readOnly />
                    <button className="btn btn--ghost" onClick={() => setAudioUrl("")}>✕</button>
                  </div>
                ) : (
                  <label className="uploader__drop" style={{ width: "100%", height: 44, borderRadius: 12 }}>
                    {uploadingAudio ? "Uploading…" : "+ Upload audio (mp3/wav)"}
                    <input type="file" accept="audio/*" hidden onChange={pickAudio} />
                  </label>
                )}
              </div>

              <div className="field">
                <span>Start frame (optional)</span>
                {startImageUrl ? (
                  <div className="uploader__thumb" style={{ width: 90, height: 90 }}>
                    <img src={startImageUrl} alt="start frame" />
                    <button onClick={() => setStartImageUrl("")}>✕</button>
                  </div>
                ) : (
                  <label className="uploader__drop" style={{ width: 90, height: 90 }}>
                    {uploadingImg ? "…" : "+ Frame"}
                    <input type="file" accept="image/*" hidden onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return; setUploadingImg(true);
                      try { setStartImageUrl(await uploadToTempUrl(f)); } catch (er) { setErr(er.message); } finally { setUploadingImg(false); }
                    }} />
                  </label>
                )}
              </div>

              <label className="field">
                <span>CFG scale {cfgScale !== "" ? `· ${cfgScale}` : ""}</span>
                <input type="range" min="0" max="20" step="0.5" value={cfgScale === "" ? 7.5 : cfgScale} onChange={(e) => setCfgScale(e.target.value)} />
              </label>

              <label className="field">
                <span>Shot type</span>
                <input type="text" value={shotType} onChange={(e) => setShotType(e.target.value)} placeholder="e.g. close-up, wide, aerial" />
              </label>

              <button className={`toggle ${voiceControl ? "toggle--on" : ""}`} onClick={() => setVoiceControl((v) => !v)}>
                <span>🎙️ Voice control (Director model)</span>
                <span className="toggle__knob" />
              </button>
            </>
          )}

          {err && <div className="form-error">{err}</div>}
          {lowCredits && (
            <button className="btn btn--primary btn--block" onClick={onNeedCredits}>💎 Buy credits</button>
          )}

          <button className="btn btn--primary btn--block" onClick={generate} disabled={busy || (needsImage ? !imageUrl : !prompt.trim())}>
            {busy ? status || "Rendering…" : "▶ Render video"}
          </button>
        </div>

        <div className="studio__out">
          <span className="studio__outlabel">Preview</span>
          <div className="videobox" style={{ aspectRatio: aspect.replace(":", "/") }}>
            {videoUrl ? (
              <video src={videoUrl} controls autoPlay loop />
            ) : busy ? (
              <div className="videobox__loading">
                <div className="spinner" />
                <span>{status || "Rendering…"}</span>
                <small>You can leave this page — it keeps rendering.</small>
              </div>
            ) : (
              <div className="videobox__empty">Your video will appear here.</div>
            )}
          </div>
          {videoUrl && video?.video_id && (
            <button className="btn btn--ghost btn--block" onClick={() => drift.share({ drop_id: video.video_id, type: "video" }).then(() => alert("Shared to Drift!")).catch((e) => alert(e.message))}>
              Post to Drift
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
